import type {
  GenericRequest,
  ImageType,
  PaginationMeta,
  PaginationQuery,
  SEO,
} from '@eliadmin/core/src/api/api-types';
import { buildAuthHeader, fetchData } from '@eliadmin/core/src/api/fetcher';
import {
  buildStoreListRequest,
  storeApiResources,
  type ApiSort,
  type ApiWhere,
} from '@eliadmin/core/src/api/store-list-request';
import type { JobPortalCategory } from '@eliadmin/core/src/api/jobportal/categories-api';
import type { JobApplyFormValues } from '@eliadmin/core/src/components/jobportal/job-apply/job-apply-form';
import type { CartPayment } from '@eliadmin/core/src/api/cart/cart-types';

export type JobPortalOfferTag = {
  id: number;
  name: string;
  slug?: string;
  color?: string;
};

export type JobPortalOffer = {
  id: number;
  name: string;
  slug: string;
  paid_at?: string;
  date_start: string;
  status?: 'draft' | 'live' | 'archive';
  is_paid: boolean;
  topped_up_to?: string | null;
  expires_at?: string;
  position_type: string;
  employment_type: null | { id: number; name: string };
  is_favorite: boolean;
  company_contact_url?: string | null;
  currency?: string | null;
  sent_interest?: {
    id: number;
    curriculum_vitae: {
      id: number;
      name: string;
      surname: string;
      additional_information: string;
      address: string;
      street_number: string;
      postcode: string;
      city: string;
      age: number;
      is_favorite: boolean;
      created_at: string;
    };
    status: any;
    created_at: string;
  };
  company?: {
    id: number;
    name: string;
    image?: ImageType;
    slug: string;
    customer: {
      id: number;
      active_subscriptions: any[];
    };
  };
  created_at: string;
  published_at?: string | null;
  city: string;
  web_url: string;
  salary?: string;
  salary_type: { id: string; name: string } | null;
  max_salary?: string;
  min_salary?: string;
  image: ImageType | null;
  countries: { id: string; name: string; is_default: boolean }[];
  tags: JobPortalOfferTag[];
  categories: JobPortalCategory[];
  match?: {
    total_weights: number;
    value: number;
  };
  country?: {
    id: string;
    name: string;
    is_default: boolean;
  } | null;
};

export type JobPortalOfferDetail = JobPortalOffer & {
  values: {
    id: number;
    name: string;
  }[];
  suitability: {
    id: number;
    name: string;
  };
  description: string;
  short_description: string;
  company: {
    id: number;
    slug: string;
    name: string;
    offers_count: number;
    customer: {
      active_subscriptions: any[];
    };
    image?: ImageType | null;
    city?: string | null;
  } | null;
  location: string | null;
  images: ImageType[];
  address: string;
  street_number: string;
  free_places: number;
  contact_name: string;
  contact_surname: string;
  contact_email: string;
  contact_phone: string;
  postcode: string;
  attachments: unknown[];
  text_fields: unknown[];
  workplace?: {
    id: number;
    name: string;
  } | null;
  age_range?: {
    id: number;
    name: string;
    age: number;
  } | null;
  working_hour?: {
    id: number;
    name: string;
  } | null;
  working_hours?: {
    working_hour: { id: number; name: string };
    start?: string | null;
    stop?: string | null;
  }[];
  graduate?: {
    id: number;
    name: string;
  } | null;
  discipline?: {
    id: number;
    name: string;
  } | null;
  prerequisite?: {
    id: number;
    name: string;
    interval_years_from: string;
    interval_years_to: string;
  } | null;
  gender?: any;
  skills?: unknown[];
  languages?: {
    language: {
      id: number;
      name: string;
      locale: string;
      is_default: boolean;
    };
    level: string;
  }[];
  driving_licences?: {
    id: number;
    name: string;
  }[];
  education?: any[];
  seo?: SEO;
};

type JobPortalOffers = {
  data: JobPortalOffer[];
  meta: PaginationMeta;
  salary_type: {
    '1': { min: string; max: string };
    '2': { min: string; max: string };
    overall: { min: string; max: string };
  };
};

export type FetchOffersRequest = GenericRequest<{
  query: {
    orderBy?: 'id' | 'salary' | 'created_at' | 'published_at' | 'paid_at' | 'min_salary' | 'distance';
    order?: 'asc' | 'desc';
    'orderBy[]'?: any[];
    'order[]'?: any[];
    results?: number;
    search?: string;
    page?: number;
    where?: ApiWhere<typeof storeApiResources.storefrontJobPortalOffers>;
    sort?: ApiSort<typeof storeApiResources.storefrontJobPortalOffers>;
    favorite?: boolean;
    topped_up_to?: string;
    status?: string;
    is_paid?: boolean;
  };
  authToken?: string;
  cache?: RequestCache;
  signal?: AbortSignal;
}>;

export const fetchOffers = async ({ locale, query, authToken, cache, signal }: FetchOffersRequest) => {
  const { where, sort, ...queryParams } = query ?? {};
  return fetchData<JobPortalOffers>({
    ...buildStoreListRequest(storeApiResources.storefrontJobPortalOffers, {
      query: queryParams,
      where,
      sort,
    }),
    locale,
    headers: buildAuthHeader(authToken),
    cache,
    signal,
  });
};

export type FetchCustomerJobOfferDetailRequest = GenericRequest<{
  slug: string;
  authToken?: string;
  cache?: RequestCache;
}>;

/** Full owner-only detail, including drafts and archived or expired offers. */
export const fetchCustomerJobOfferDetail = async ({
  locale,
  slug,
  authToken,
  cache,
}: FetchCustomerJobOfferDetailRequest) => {
  return fetchData<{ data: JobPortalOfferDetail }>({
    method: 'GET',
    locale,
    uri: `/job-portal/customer/offers/${slug}`,
    headers: buildAuthHeader(authToken),
    cache: cache ?? 'no-store',
  });
};

export type FetchJobPortalEmploymentTypesQuery = PaginationQuery;
export type JobPortalEmploymentType = {
  id: number;
  name: string;
};

export function fetchJobPortalEmploymentTypes({
  query,
  ...request
}: GenericRequest<{ query: FetchJobPortalEmploymentTypesQuery }>) {
  return fetchData<{ data: JobPortalEmploymentType[]; meta: PaginationMeta }>({
    ...buildStoreListRequest(storeApiResources.storefrontJobPortalEmploymentTypes, { query }),
    ...request,
  }).then(r => ({
    ...r,
    data: r.data.filter(item => item.id !== 5),
  }));
}

export type FetchJobPortalSalariesQuery = PaginationQuery;
export type JobPortalSalary = {
  id: number;
  name: string;
};

export function fetchJobPortalSalaries(
  req: GenericRequest<{ query: FetchJobPortalSalariesQuery }>,
) {
  return fetchData<{ data: JobPortalSalary[] }>({
    method: 'GET',
    uri: '/job-portal/salaries',
    ...req,
  });
}

export function applyForJob(req: GenericRequest<{ data: JobApplyFormValues }>) {
  return fetchData({
    method: 'POST',
    uri: '/job-portal/interests',
    body: req.data,
    ...req,
  });
}

export const addFavoriteOffer = async ({
  authToken,
  slug,
  locale,
}: {
  authToken: string;
  slug: string;
  locale?: string;
}) => {
  return fetchData({
    method: 'PUT',
    uri: `/job-portal/offers/${slug}/set-favorite`,
    headers: buildAuthHeader(authToken),
    locale,
  });
};

export const addToFavoriteCurriculum = async ({
  authToken,
  slug,
  locale,
}: {
  authToken: string;
  slug: string;
  locale?: string;
}) => {
  return fetchData({
    method: 'PUT',
    uri: `/job-portal/curriculums/${slug}/set-favorite`,
    headers: buildAuthHeader(authToken),
    locale,
  });
};

export const addToFavoriteCourse = async ({
  authToken,
  slug,
  locale,
}: {
  authToken: string;
  slug: string;
  locale?: string;
}) => {
  return fetchData({
    method: 'PUT',
    uri: `/course/courses/${slug}/set-favorite`,
    headers: buildAuthHeader(authToken),
    locale,
  });
};

export const addToFavoriteCompany = async ({
  authToken,
  slug,
  locale,
}: {
  authToken: string;
  slug: string;
  locale: string;
}) => {
  return fetchData({
    method: 'PUT',
    uri: `/job-portal/companies/${slug}/set-favorite`,
    headers: buildAuthHeader(authToken),
    locale,
  });
};

export const addToFavoriteSchool = async ({
  authToken,
  slug,
  locale,
}: {
  authToken: string;
  slug: string;
  locale: string;
}) => {
  return fetchData({
    method: 'PUT',
    uri: `/job-portal/schools/${slug}/set-favorite`,
    headers: buildAuthHeader(authToken),
    locale,
  });
};

export const deleteJobOffer = async ({
  authToken,
  slug,
  locale,
}: {
  authToken: string;
  slug: string;
  locale?: string;
}) => {
  return fetchData({
    method: 'DELETE',
    uri: `/job-portal/customer/offers/${slug}`,
    headers: buildAuthHeader(authToken),
    locale,
  });
};

export const payCourse = async ({
  courseSlug,
  authToken,
  body,
}: {
  courseSlug: string;
  authToken: string;
  body: {
    payment_type?: {
      id: number;
    };
    variants: {
      id: number;
    }[];
    course_balance_free: boolean;
    course_topping_balance_free: boolean;
    return_url: string;
  };
}) => {
  return fetchData<any>({
    uri: `/course/courses/${courseSlug}/pay`,
    method: 'POST',
    headers: buildAuthHeader(authToken),
    body,
  });
};

export type PaymentType = CartPayment;

export type FetchPaymentTypesRequest = GenericRequest<{
  query?: {
    results?: number;
  };
  authToken?: string;
}>;

export const fetchPaymentTypes = async ({ locale, authToken }: FetchPaymentTypesRequest) => {
  return fetchData<{ data: PaymentType[] }>({
    ...buildStoreListRequest(storeApiResources.storePaymentTypes),
    locale,
    headers: buildAuthHeader(authToken),
  });
};
